import React from "react";
import { X, Image as ImageIcon, FileText, Download, Loader2 } from "lucide-react";
import type { TeamAttachment } from "@/types/communication";
import type { ChatGroupDetails } from "@/redux/api/teamChatApi";
import { type ActiveChatState, getDepartmentIcon } from "./types";

interface ChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activeChat: ActiveChatState;
  drawerTab: "Members" | "Files";
  setDrawerTab: (tab: "Members" | "Files") => void;
  groupDetails?: ChatGroupDetails;
  isLoadingGroupDetails?: boolean;
  sharedFiles: TeamAttachment[];
}

export const ChatDrawer: React.FC<ChatDrawerProps> = ({
  isOpen,
  onClose,
  activeChat,
  drawerTab,
  setDrawerTab,
  groupDetails,
  isLoadingGroupDetails,
  sharedFiles,
}) => {
  if (!isOpen) return null;

  const groupMembers = groupDetails?.members || [];
  const adminIds = new Set((groupDetails?.admins || []).map((a) => a._id));
  const memberCount = groupMembers.length || activeChat.membersCount || 0;

  return (
    <div className="absolute right-0 top-0 bottom-0 lg:relative w-72 lg:w-80 border-l border-gray-200 bg-white flex flex-col shadow-2xl lg:shadow-none z-20 animate-in slide-in-from-right duration-300">
      <div className="p-3 border-b border-gray-100 flex items-center justify-between">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
          {activeChat.type === "group"
            ? "Group Details"
            : activeChat.type === "customer"
            ? "Customer Info"
            : "User Info"}
        </span>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          <X size={18} />
        </button>
      </div>

      <div className="px-4 py-6 text-center border-b border-gray-100">
        <div
          className={`w-16 h-16 ${
            activeChat.color || "bg-[#1D51A4]"
          } rounded-2xl mx-auto flex items-center justify-center text-white shadow-md mb-3 overflow-hidden font-bold text-xl`}
        >
          {activeChat.type === "group" ? (
            getDepartmentIcon(activeChat.category)
          ) : activeChat.avatar ? (
            <img
              src={activeChat.avatar}
              alt={activeChat.name}
              className="w-full h-full object-cover"
            />
          ) : (
            activeChat.name.charAt(0).toUpperCase()
          )}
        </div>
        <h3 className="text-base font-bold text-[#051321]">{activeChat.name}</h3>
        <p className="text-xs text-[#637381] mt-0.5">
          {activeChat.type === "group"
            ? `${memberCount} members`
            : activeChat.type === "customer"
            ? "Customer"
            : activeChat.role || "Staff Member"}
        </p>
      </div>

      {/* Drawer Tabs */}
      <div className="flex border-b border-gray-100 px-4">
        <button
          className={`flex-1 py-2.5 text-xs font-semibold relative transition-colors cursor-pointer ${
            drawerTab === "Members" ? "text-blue-600" : "text-gray-400"
          }`}
          onClick={() => setDrawerTab("Members")}
        >
          {activeChat.type === "group" ? `Members (${memberCount})` : "Details"}
          {drawerTab === "Members" && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600" />
          )}
        </button>
        <button
          className={`flex-1 py-2.5 text-xs font-semibold relative transition-colors cursor-pointer ${
            drawerTab === "Files" ? "text-blue-600" : "text-gray-400"
          }`}
          onClick={() => setDrawerTab("Files")}
        >
          Shared Files ({sharedFiles.length})
          {drawerTab === "Files" && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600" />
          )}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {drawerTab === "Members" ? (
          activeChat.type === "group" ? (
            isLoadingGroupDetails ? (
              <div className="p-6 text-center text-xs text-gray-400">
                <Loader2 className="animate-spin h-5 w-5 mx-auto mb-2 text-blue-500" />
                Loading members...
              </div>
            ) : groupMembers.length === 0 ? (
              <div className="text-center text-xs text-gray-400 py-6">
                No members found
              </div>
            ) : (
              groupMembers.map((member) => {
                const isAdmin = adminIds.has(member._id);
                return (
                  <div
                    key={member._id}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 font-bold flex items-center justify-center text-xs shrink-0">
                        {member.name ? member.name.charAt(0).toUpperCase() : "U"}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-[#051321] truncate">
                          {member.name}
                        </p>
                        <p className="text-[10px] text-gray-400 capitalize truncate">
                          {member.role || "Staff"}
                        </p>
                      </div>
                    </div>
                    {isAdmin && (
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">
                        Admin
                      </span>
                    )}
                  </div>
                );
              })
            )
          ) : activeChat.type === "customer" ? (
            <div className="p-3 bg-slate-50 rounded-xl text-xs space-y-2.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Contact:</span>
                <span className="font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full text-[10px]">
                  Customer
                </span>
              </div>
              {activeChat.email && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500">Email:</span>
                  <span className="font-medium text-slate-800 truncate max-w-35" title={activeChat.email}>
                    {activeChat.email}
                  </span>
                </div>
              )}
              {activeChat.phone && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Phone:</span>
                  <span className="font-medium text-slate-800">
                    {activeChat.phone}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Customer ID:</span>
                <span className="font-mono text-[10px] text-slate-700 truncate max-w-35">
                  {activeChat.id}
                </span>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-slate-50 rounded-xl text-xs space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Role:</span>
                <span className="font-semibold text-slate-800 capitalize">
                  {activeChat.role || "Staff"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status:</span>
                <span className="font-semibold text-emerald-600">Active</span>
              </div>
            </div>
          )
        ) : sharedFiles.length === 0 ? (
          <div className="text-center text-xs text-gray-400 py-10">
            No files shared in this chat yet
          </div>
        ) : (
          <div className="space-y-2">
            {sharedFiles.map((file, fIdx) => (
              <a
                key={fIdx}
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 p-2 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
              >
                {file.type?.startsWith("image/") ? (
                  <ImageIcon size={18} className="text-blue-500 shrink-0" />
                ) : (
                  <FileText size={18} className="text-emerald-500 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-slate-800 truncate">
                    {file.name}
                  </p>
                  {file.size && (
                    <p className="text-[10px] text-slate-400">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  )}
                </div>
                <Download size={14} className="text-slate-400 shrink-0" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatDrawer;
